import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarIcon } from 'lucide-react';
import {
  Avatar,
  BackLink,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  PageEyebrow,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { eventStageTone } from '@/lib/event-status';
import { eventStartMs, formatEventDate } from '@/lib/event-time';
import {
  CONTACT_STALE_MS,
  type ContactEventRow,
  type ContactRecord,
} from '@/pages/contacts-shared';

/**
 * One client contact, laid out like a person profile (the People directory's
 * page): identity header, fact list, then upcoming and past events. No
 * sign-in history — contacts aren't portal users. Internal staff only; the
 * routes 403 everyone else.
 */

/** The event fields each section's search matches. */
const CONTACT_EVENT_SEARCH_FIELDS: ReadonlyArray<keyof ContactEventRow> = ['name', 'contractNumber', 'accountName', 'program', 'stageName'];

/**
 * One list of the contact's events. Its search is local state, not `?q=`:
 * the page shows upcoming and past side by side, each searched on its own.
 */
function EventsSection({
  title,
  events,
  empty,
}: {
  readonly title: string;
  readonly events: readonly ContactEventRow[];
  readonly empty: string;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const rows = filterRows(events, search, CONTACT_EVENT_SEARCH_FIELDS);
  return (
    <div>
      <h2 className="mb-2 text-portal-section font-bold text-portal-ink">
        {title} ({events.length})
      </h2>
      {events.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <SearchInput
            label={`Search ${title.toLowerCase()}`}
            placeholder="Event, contract, client, program or stage"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>
      ) : null}
      {events.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<CalendarIcon aria-hidden className="size-6" />}
            title={empty}
            message=""
          />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState title="No events match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable headers={['Event', 'Client', 'Date', 'Program', 'Stage']} minWidth={820}>
          {rows.map((event) => (
            <tr key={event.id} className="border-t border-portal-line align-top">
              <td className="p-3">
                <Link
                  to={`/events/${event.id}`}
                  className="text-portal-small font-bold text-brand hover:underline"
                >
                  {event.name || event.contractNumber || 'Untitled event'}
                </Link>
                {event.contractNumber ? (
                  <span className="block font-mono text-portal-micro text-portal-muted">
                    {event.contractNumber}
                  </span>
                ) : null}
              </td>
              <td className="p-3 text-portal-small">
                {event.accountName && event.accountId ? (
                  <Link to={`/accounts/${event.accountId}`} className="text-brand hover:underline">
                    {event.accountName}
                  </Link>
                ) : (
                  <span className="text-portal-muted">{event.accountName ?? '—'}</span>
                )}
              </td>
              <td className="p-3 whitespace-nowrap text-portal-small">
                {event.eventDate ? (
                  <Link
                    to={`/places/events?onDate=${event.eventDate.slice(0, 10)}`}
                    title="See everything on this date"
                    className="text-brand hover:underline"
                  >
                    {formatEventDate(event, 'medium')}
                  </Link>
                ) : (
                  <span className="text-portal-muted">—</span>
                )}
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{event.program ?? '—'}</td>
              <td className="p-3 whitespace-nowrap">
                {event.stageName ? (
                  <Pill tone={eventStageTone(event.stageName)}>{event.stageName}</Pill>
                ) : (
                  <span className="text-portal-small text-portal-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}

export function ContactPage(): React.ReactElement {
  const { id = '' } = useParams();

  const contact = useQuery({
    queryKey: ['contact', id],
    queryFn: ({ signal }) => getJson<ContactRecord>(`/contacts/${id}`, undefined, signal),
    enabled: id.length === 24,
    staleTime: CONTACT_STALE_MS,
  });
  const events = useQuery({
    queryKey: ['contact-events', id],
    queryFn: ({ signal }) => getJson<ContactEventRow[]>(`/contacts/${id}/events`, undefined, signal),
    enabled: id.length === 24,
    staleTime: CONTACT_STALE_MS,
  });

  if (contact.isPending) return <SkeletonTable label="Loading contact…" />;
  if (contact.isError) {
    return <ErrorNotice title="Contact unavailable" message={errorMessage(contact.error)} />;
  }
  const person = contact.data;
  const name = person.name || person.email;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const all = events.data ?? [];
  // Split on the event's real start instant, not the drifted `eventDate` —
  // otherwise an event happening today can file itself under yesterday.
  const startsAt = (event: (typeof all)[number]): number | undefined => eventStartMs(event);
  const upcoming = all
    .filter((event) => (startsAt(event) ?? -Infinity) >= today.getTime())
    .sort((a, b) => (startsAt(a) ?? 0) - (startsAt(b) ?? 0));
  const past = all.filter((event) => (startsAt(event) ?? -Infinity) < today.getTime());

  return (
    <section className="animate-portal-rise">
      <BackLink to="/contacts">Contacts</BackLink>

      {/* Identity header — same grammar as the person profile. */}
      <div className="mt-3 mb-[27px] flex flex-wrap items-center gap-4">
        <Avatar name={name} size={56} />
        <div className="min-w-0 flex-1">
          <PageEyebrow>CLIENT CONTACT</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1px]">{name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {person.roles.map((role) => (
              <Pill key={role}>{role}</Pill>
            ))}
            {person.accounts.length > 0 ? (
              <span className="text-portal-small text-portal-muted">
                {person.accounts.join(' · ')}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <Panel className="mb-6 p-5">
        <FactList
          columns={2}
          facts={[
            { label: 'Email', value: person.email },
            { label: 'Phone', value: person.phone || undefined },
            { label: 'Mobile', value: person.cell || undefined },
            { label: 'Company / account', value: person.accounts.join(', ') || undefined },
            { label: 'Roles on events', value: person.roles.join(', ') || undefined },
            {
              label: 'Last event',
              value: person.lastEventAt
                ? new Date(person.lastEventAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : undefined,
            },
          ]}
        />
      </Panel>

      {events.isPending ? (
        <SkeletonTable label="Loading events…" />
      ) : events.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(events.error)} />
      ) : (
        <div className="space-y-8">
          <EventsSection
            title="Upcoming events"
            events={upcoming}
            empty="No upcoming events"
          />
          <EventsSection title="Past events" events={past} empty="No past events" />
        </div>
      )}
    </section>
  );
}
